'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { ResponsiveImage } from '@/app/components/media/app-image';
import type { SizesString } from '@/app/components/media/app-image/shared/image-size-types';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { SPARTAN_PRODUCT_ENGINE_LOGO, SPARTAN_PRODUCT_ENGINE_LOGO_DESKTOP_WIDTHS, SPARTAN_PRODUCT_ENGINE_LOGO_MOBILE_SIZES } from '@/app/lcv/components/sections/spartan/products/engine/constants';
import type { SpartanProductEngineLogo } from '@/app/lcv/components/sections/spartan/products/engine/types';
import { getEngineLogoIdentity, getEngineLogoMobileSrc } from '@/app/lcv/components/sections/spartan/products/engine/utils';
import type { SpartanProductEngineAnimatedLogoProps } from '@/app/lcv/components/sections/spartan/products/engine/view/card/animated-logo/types';
import { clamp, setHorizontalBlur } from '@/app/lcv/components/sections/spartan/products/engine/view/card/animated-logo/utils';
import { cn } from '@/app/utils/cn';
import styles from './styles.module.css';

/** Match SpeedingText / animated-value timing; blur-x peaks mid-swap. */
const DURATION_MS = 200;
const MAX_BLUR = 24;
type DisplayedLogo = {
  logo: SpartanProductEngineLogo;
  alt: string;
};
export const SpartanProductEngineAnimatedLogo = ({
  logo,
  alt,
  testId,
  className,
  loading,
  fetchPriority
}: SpartanProductEngineAnimatedLogoProps) => {
  const reactId = useId().replace(/:/g, '');
  const filterId = `${reactId}-logo-blur`;
  const [displayedLogo, setDisplayedLogo] = useState<DisplayedLogo>({
    logo,
    alt
  });
  const rootRef = useRef<HTMLDivElement | null>(null);
  const blurRef = useRef<SVGFEGaussianBlurElement | null>(null);
  const displayedIdentityRef = useRef(getEngineLogoIdentity(logo));
  const pendingLogoRef = useRef<DisplayedLogo>({
    logo,
    alt
  });
  const isFirstRenderRef = useRef(true);
  const hasSwappedRef = useRef(true);
  const rafRef = useRef<number | null>(null);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', false) ?? false;
  const {
    mobile: mobileLogoSize,
    desktop: desktopLogoSize
  } = SPARTAN_PRODUCT_ENGINE_LOGO;
  useEffect(() => {
    const nextLogo = {
      logo,
      alt
    };
    pendingLogoRef.current = nextLogo;
    const setAnimating = (active: boolean) => {
      rootRef.current?.classList.toggle(styles.animating, active);
    };
    const settle = (value: DisplayedLogo) => {
      displayedIdentityRef.current = getEngineLogoIdentity(value.logo);
      pendingLogoRef.current = value;
      hasSwappedRef.current = true;
      setDisplayedLogo(value);
      setAnimating(false);
      setHorizontalBlur(blurRef.current, 0);
    };
    const cancelRaf = () => {
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      settle(nextLogo);
      return () => cancelRaf();
    }
    if (getEngineLogoIdentity(logo) === displayedIdentityRef.current) {
      setDisplayedLogo(nextLogo);
      return () => cancelRaf();
    }
    cancelRaf();
    if (reducedMotion) {
      settle(nextLogo);
      return () => cancelRaf();
    }
    hasSwappedRef.current = false;
    setAnimating(true);
    const startedAt = performance.now();
    const frame = (now: number) => {
      const progress = clamp((now - startedAt) / DURATION_MS, 0, 1);
      // Rise and fall so the smear peaks through the logo swap.
      const blur = Math.sin(progress * Math.PI) * MAX_BLUR;
      setHorizontalBlur(blurRef.current, blur);
      if (!hasSwappedRef.current && progress >= 0.5) {
        hasSwappedRef.current = true;
        displayedIdentityRef.current = getEngineLogoIdentity(pendingLogoRef.current.logo);
        setDisplayedLogo(pendingLogoRef.current);
      }
      if (progress < 1) {
        rafRef.current = window.requestAnimationFrame(frame);
        return;
      }
      settle(pendingLogoRef.current);
      rafRef.current = null;
    };
    setHorizontalBlur(blurRef.current, 0);
    rafRef.current = window.requestAnimationFrame(frame);
    return () => {
      cancelRaf();
      setAnimating(false);
    };
  }, [logo, alt, reducedMotion]);
  const mobileSrc = getEngineLogoMobileSrc(displayedLogo.logo);
  const desktopSrc = displayedLogo.logo.src;
  const desktopLogoSizes = `${desktopLogoSize.width}px` as const satisfies SizesString;
  return <div ref={rootRef} className={cn('relative overflow-visible', className)}>
      <svg aria-hidden className="absolute size-0 overflow-hidden">
        <defs>
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur ref={blurRef} in="SourceGraphic" stdDeviation="0,0" />
          </filter>
        </defs>
      </svg>

      <div className={styles.media} style={{
      filter: `url(#${filterId})`,
      ['--engine-logo-aspect-mobile' as string]: `${mobileLogoSize.width} / ${mobileLogoSize.height}`,
      ['--engine-logo-aspect-desktop' as string]: `${desktopLogoSize.width} / ${desktopLogoSize.height}`
    }}>
        <ResponsiveImage mobileSrc={{
        src: mobileSrc,
        sizes: SPARTAN_PRODUCT_ENGINE_LOGO_MOBILE_SIZES
      }} desktopSrc={{
        src: desktopSrc,
        sizes: desktopLogoSizes,
        widths: SPARTAN_PRODUCT_ENGINE_LOGO_DESKTOP_WIDTHS
      }} alt={displayedLogo.alt} width={mobileLogoSize.width} height={mobileLogoSize.height} fade={false} loading={loading} fetchPriority={fetchPriority} objectFit="contain" className={styles.picture} imgClassName="w-full" testId={testId} />
      </div>
    </div>;
};